import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, TrendingUp } from 'lucide-react';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { motion } from 'framer-motion';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

import { baseURL } from '@/config/APIConfig';


const API = baseURL.dashboardApi;

const FinancialOverview = () => {
    const [financialData, setFinancialData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchFinancialData();
    }, []);

    const fetchFinancialData = async () => {
        try {
            const response = await axios.get(`${API}/financial/overview`);
            setFinancialData(response.data);
        } catch (error) {
            console.error('Error fetching financial data:', error);
        } finally {
            setLoading(false);
        }
    };

    const monthlyTrendChartData = financialData ? {
        labels: financialData?.monthly_trend?.map(m => m.month),
        datasets: [
            {
                label: 'Monthly Spend (USD)',
                data: financialData?.monthly_trend?.map(m => m.spend),
                borderColor: '#10b981',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                tension: 0.4,
                fill: true,
            },
        ],
    } : null;

    const routeCostsChartData = financialData ? {
        labels: financialData?.route_costs?.map(r => r.route),
        datasets: [
            {
                label: 'Total Cost (USD)',
                data: financialData?.route_costs?.map(r => r.total_cost),
                backgroundColor: '#3b82f6',
                borderRadius: 8,
            },
        ],
    } : null;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                labels: {
                    color: '#94a3b8',
                },
            },
        },
        scales: {
            x: {
                ticks: { color: '#94a3b8' },
                grid: { color: 'rgba(148, 163, 184, 0.1)' },
            },
            y: {
                ticks: { color: '#94a3b8' },
                grid: { color: 'rgba(148, 163, 184, 0.1)' },
            },
        },
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 animate-pulse">
                    <div className="h-64 bg-slate-200 dark:bg-slate-800 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
            data-testid="financial-overview"
        >
            {/* AI Cost Optimization Banner */}
            <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold mb-2">Cost Optimization Suggestion</h3>
                        <p className="text-white/90">
                            Switch carrier from MSC to CMA CGM for Singapore → London route to save <span className="font-bold">8% ($2,400)</span> on average shipment costs.
                        </p>
                    </div>
                </div>
            </div>

            {/* Monthly Freight Spend Trend */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">Monthly Freight Spend Trend</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Last 6 months overview</p>
                    </div>
                    <div className="flex items-center space-x-2 text-green-500">
                        <TrendingUp className="w-5 h-5" />
                        <span className="font-semibold">+12% vs last quarter</span>
                    </div>
                </div>
                {monthlyTrendChartData && (
                    <div style={{ height: '300px' }}>
                        <Line data={monthlyTrendChartData} options={chartOptions} />
                    </div>
                )}
            </div>

            {/* Cost Breakdown by Route */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">Freight Cost by Route</h3>
                {routeCostsChartData && (
                    <div style={{ height: '300px' }}>
                        <Bar data={routeCostsChartData} options={chartOptions} />
                    </div>
                )}
            </div>

            {/* Detailed Cost Breakdown Table */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">Detailed Cost Breakdown</h3>
                <div className="overflow-x-auto">
                    <table className="w-full" data-testid="cost-breakdown-table">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Route</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Freight</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Bunker</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">THC</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Documentation</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {financialData?.route_costs?.map((cost, index) => (
                                <tr
                                    key={index}
                                    data-testid={`cost-row-${index}`}
                                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                    <td className="py-4 px-4 text-sm font-medium text-slate-900 dark:text-white">{cost.route}</td>
                                    <td className="py-4 px-4 text-right text-sm text-slate-600 dark:text-slate-400">
                                        ${cost.freight_cost.toLocaleString()}
                                    </td>
                                    <td className="py-4 px-4 text-right text-sm text-slate-600 dark:text-slate-400">
                                        ${cost.bunker_cost.toLocaleString()}
                                    </td>
                                    <td className="py-4 px-4 text-right text-sm text-slate-600 dark:text-slate-400">
                                        ${cost.thc_cost.toLocaleString()}
                                    </td>
                                    <td className="py-4 px-4 text-right text-sm text-slate-600 dark:text-slate-400">
                                        ${cost.documentation_cost.toLocaleString()}
                                    </td>
                                    <td className="py-4 px-4 text-right text-sm font-semibold text-slate-900 dark:text-white">
                                        ${cost.total_cost.toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </motion.div>
    );
};

export default FinancialOverview;